import type { ContactProps } from '@/components/landing-page/components/contact';
import type { State } from '@/components/control/types';
import { useState } from 'react';
import axios from 'axios';
import clsx from 'clsx';
import { CheckCircle } from 'lucide-react';
import * as v from 'valibot';
import { useToggle } from 'usehooks-ts';
import { defaults } from '@/components/landing-page/components/contact';
import Dialog from '@/components/dialog';
import TextField from '@/components/text-field';
import Button from '@/components/button';
import Control from '@/components/control';
import { useAppDispatch, useAppSelector } from '@/redux/store';
import { ConfigSlice } from '@/redux/config';
import { GeneralSlice } from '@/redux/general';
import { UserFormSlice } from '@/redux/user-form';
import { cn } from '@/utils/helper';
import Track from '@/utils/track';
import Valibot from '@/utils/valibot';
import v1 from '@/components/landing-page/components/contact/contact-v1.module.css';
import v2 from '@/components/landing-page/components/contact/contact-v2.module.css';
import { postSendFeedbackMail } from '@/api/requests';

const Schema = v.object({
  name: v.pipe(v.string(), v.minLength(3), v.maxLength(100)),
  email: v.pipe(v.string(), v.email()),
  phone: v.pipe(v.string(), v.minLength(7), v.maxLength(30)),
  message: v.pipe(v.string(), v.maxLength(1000)),
});

type Form = v.InferInput<typeof Schema>;

const Contact = (props: ContactProps) => {
  const {
    sidebar = defaults.sidebar,
    title = defaults.title,
    main = defaults.main,
    button = defaults.button,
    success = defaults.success,
  } = props;
  const dispatch = useAppDispatch();
  const cssVersion = useAppSelector(ConfigSlice.selectors.cssVersion);
  const dataForContact = useAppSelector(UserFormSlice.selectors.dataForContact);
  const secret = useAppSelector(GeneralSlice.selectors.secret);
  const submitted = useAppSelector(GeneralSlice.selectors.isContactFormSubmitted);
  const [userForm, setUserForm] = useState<Partial<Form>>({});
  const [dirtyFields, setDirtyFields] = useState(Valibot.RecordFromSchema<Form, boolean>(Schema, false));
  const [loading, toggleLoading] = useToggle(false);
  const [isDialogOpen, toggleIsDialogOpen] = useToggle(false);

  const form = {
    ...{
      name: dataForContact.name,
      email: dataForContact.email,
      phone: dataForContact.phone,
      message: '',
    },
    ...userForm,
  };

  const { valid, statuses, output } = Valibot.Result<Form>(Schema, form);

  const submitContactForm = async (): Promise<void> => {
    toggleLoading();
    try {
      await postSendFeedbackMail({ ...output, secret });
      dispatch(GeneralSlice.actions.setIsContactFormSubmitted(true));
      toggleIsDialogOpen();
      toggleLoading();
      Track.DataLayer.ContactFormSent();
    } catch {
      // TODO: We do not handle error case. Just close form. Maybe think about some UI reaction.
      toggleIsDialogOpen();
      toggleLoading();
    }
  };

  const openDialog = (): void => {
    toggleIsDialogOpen();
    Track.DataLayer.LongReadClick();
  };

  const state = (key: keyof Form, form: Partial<Form>): State => {
    const status = statuses[key];
    const dirty = dirtyFields[key];
    const value = form[key];
    if (!status && value) return 'success';
    if (status && dirty) return 'error';
    return 'idle';
  };

  const onChange = (value: string, key: keyof Form): void => {
    setUserForm((prevState) => ({ ...prevState, [key]: value }));
  };

  const onBlur = (key: keyof Form): void => {
    setDirtyFields((prevState) => ({ ...prevState, [key]: true }));
  };

  return (
    <div data-testid='contact' className={cn(v1.Contact, v2.Contact)}>
      {submitted ? (
        <div data-testid='contact-success' className={cn(v1.Success, v2.Success)}>
          <CheckCircle className={cn(v1.SuccessIcon, v2.SuccessIcon)} />
          <p className={cn(v1.SuccessText, v2.SuccessText)}>{success}</p>
        </div>
      ) : (
        <div
          data-testid='contact-wait'
          className={clsx(cn(v1.Wait, v2.Wait), { [cn(v1.WaitSidebar, v2.WaitSidebar)]: sidebar })}
        >
          <h6 className={cn(v1.Head, v2.Head)}>{title}</h6>
          <p className={cn(v1.Main, v2.Main)}>{main}</p>
          <button data-testid='contact-button' type='button' className={cn(v1.Button, v2.Button)} onClick={openDialog}>
            {button}
          </button>
        </div>
      )}
      <Dialog on={isDialogOpen} position='center' size='1/3' onClose={() => toggleIsDialogOpen()}>
        <div className={cn(v1.Form, v2.Form)}>
          <h6 className={cn(v1.FormHead, v2.FormHead)}>Kontaktieren Sie uns</h6>
          <p className={cn(v1.FormInfo, v2.FormInfo)}>
            Nutzen Sie unser Kontaktformular um bequem Ihre Frage zu stellen oder einen Rückruf zu vereinbaren. Ein
            Mitarbeiter wird sich umgehend bei Ihnen melden.
          </p>
          <div className={cn(v1.FormContent, v2.FormContent)}>
            <div className={cn(v1.FormRow, v2.FormRow)}>
              {cssVersion === 1 ? (
                <>
                  <p className={v1.Label}>Name *</p>
                  <TextField
                    full
                    name='name'
                    placeholder='Ihr Name'
                    value={form.name}
                    isDisabled={loading}
                    success={!statuses.name && !!form.name}
                    error={statuses.name && dirtyFields.name}
                    onChange={(v) => onChange(v, 'name')}
                    onBlur={() => onBlur('name')}
                  />
                </>
              ) : (
                <Control.Text
                  label='Name *'
                  id='name'
                  value={form.name}
                  disabled={loading}
                  state={state('name', form)}
                  onChange={(v) => onChange(v, 'name')}
                  onBlur={() => onBlur('name')}
                />
              )}
            </div>
            <div className={cn(v1.FormRow, v2.FormRow)}>
              {cssVersion === 1 ? (
                <>
                  <p className={v1.Label}>E-Mail *</p>
                  <TextField
                    full
                    name='email'
                    placeholder='Ihre E-Mail-Adresse'
                    value={form.email}
                    isDisabled={loading}
                    success={!statuses.email && !!form.email}
                    error={statuses.email && dirtyFields.email}
                    onChange={(v) => onChange(v, 'email')}
                    onBlur={() => onBlur('email')}
                  />
                </>
              ) : (
                <Control.Text
                  label='Ihre E-Mail-Adresse *'
                  id='email'
                  value={form.email}
                  disabled={loading}
                  state={state('email', form)}
                  onChange={(v) => onChange(v, 'email')}
                  onBlur={() => onBlur('email')}
                />
              )}
            </div>
            <div className={cn(v1.FormRow, v2.FormRow)}>
              {cssVersion === 1 ? (
                <>
                  <p className={v1.Label}>Ihr Telefon (idealerweise Handy) *</p>
                  <TextField
                    full
                    name='phone'
                    placeholder='Ihre Telefonnummer (idealerweise Handy)'
                    value={form.phone}
                    isDisabled={loading}
                    success={!statuses.phone && !!form.phone}
                    error={statuses.phone && dirtyFields.phone}
                    onChange={(v) => onChange(v, 'phone')}
                    onBlur={() => onBlur('phone')}
                  />
                </>
              ) : (
                <Control.Text
                  label='Ihre Telefonnummer (idealerweise Handy) *'
                  id='phone'
                  value={form.phone}
                  disabled={loading}
                  state={state('phone', form)}
                  onChange={(v) => onChange(v, 'phone')}
                  onBlur={() => onBlur('phone')}
                />
              )}
            </div>
            <div className={cn(v1.FormRow, v2.FormRow)}>
              {cssVersion === 1 ? (
                <>
                  <p className={v1.Label}>Ihre Frage / Nachricht</p>
                  <TextField
                    full
                    textarea
                    rows={4}
                    name='message'
                    placeholder='Ihre Frage / Nachricht'
                    value={form.message}
                    isDisabled={loading}
                    success={!statuses.message && !!userForm.message}
                    error={statuses.message && dirtyFields.message}
                    onChange={(v) => onChange(v, 'message')}
                    onBlur={() => onBlur('message')}
                  />
                </>
              ) : (
                <Control.Textarea
                  placeholder='Ihre Frage / Nachricht'
                  id='message'
                  rows={4}
                  value={form.message}
                  state={state('message', userForm)}
                  onChange={(v) => onChange(v, 'message')}
                  onBlur={() => onBlur('message')}
                />
              )}
            </div>
            <div className={cn(v1.FormSubmit, v2.FormSubmit)}>
              {cssVersion === 1 ? (
                <Button color='primary' disabled={!valid || loading} loading={loading} onClick={submitContactForm}>
                  Abfrage abschicken
                </Button>
              ) : (
                <Control.Button
                  fullWidth
                  color='next'
                  type='button'
                  size='lg'
                  disabled={!valid || loading}
                  onClick={submitContactForm}
                >
                  Abfrage abschicken
                </Control.Button>
              )}
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default Contact;
